import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../../../../../../../../redux/store";
import { closeFileDetails } from "../../../../../../../../../redux/slices/fileManagementSlice";
import {
  usePatchFileMutation,
  useGetKBFileQuery,
} from "../../../../../../../../../redux/services/kbFilesApi";
import { baseUrl } from "../../../../../../../../../redux/services/baseApi";
import Drawer from "../../../../../../../../../components/SideDrawer/SideDrawer";
import CloseIcon from "../../../../../../../../../assets/svg/close-icon";
import EditIcon from "../../../../../../../../../assets/svg/edit-icon";
import ChevronDownIcon from "../../../../../../../../../assets/svg/chevron-down-icon";
import DownloadIcon from "../../../../../../../../../assets/svg/download-icon";
import CheckIcon from "../../../../../../../../../assets/svg/check-icon";
import type {
  FileDetailsDrawerProps,
  KBFileEntity,
  KBFileVisibility,
} from "../../types/fileManagement.types";
import {
  FILE_DETAILS_SECTIONS,
  FILE_DETAILS_DRAWER_WIDTH,
  FILE_DETAILS_OVERLAY_OPACITY,
  FILE_DETAILS_DRAWER_TITLE,
  FILE_DETAILS_MORE_BUTTON,
  FILE_DETAILS_DOWNLOAD_OPTION,
  FILE_DETAILS_DOWNLOADING_OPTION,
  FIELD_LABEL_DIMENSIONS,
  PLACEHOLDER_EMPTY,
  TOAST_FILE_DOWNLOAD_SUCCESS,
  TOAST_FILE_DOWNLOAD_FAILED,
  TOAST_FILE_VISIBILITY_UPDATED,
  TOAST_FILE_EXPIRATION_UPDATED,
  TOAST_FILE_NAME_UPDATED,
} from "../../constants/fileManagement.constants";
import {
  localDateToUtcIsoString,
  triggerFileDownloadByUrl,
  buildDrawerBreadcrumbs,
  parseExpirationDateTime,
  getEarliestQuarterHourFromNow,
  getCurrentHHMM,
  showSuccessToast,
  showErrorToast,
} from "../../utils/fileManagement.utils";
import ClipLoader from "../../../../../../../../../components/Common/ClipLoader";
import { useEventChangeOutside } from "../../../../../../../../../hooks/useEventChangeOutside";
import FileDetailsDrawerShimmer from "../Shimmer/FileDetailsDrawerShimmer";
import FileDetailsAccessAndUrls from "./components/FileDetailsAccessAndUrls";
import FileDetailsFileHistory from "./components/FileDetailsFileHistory";
import FileDetailsGeneral from "./components/FileDetailsGeneral";
import FileDetailsAccordion from "./components/FileDetailsAccordion";
import FilePreviewSection from "./components/FilePreviewSection";

const FileDetailsDrawer = ({ path, viewMode }: FileDetailsDrawerProps) => {
  const dispatch = useDispatch();
  const selectedFile = useSelector(
    (state: RootState) => state.fileManagement.selectedFile
  );
  const isFileDetailsOpen = useSelector(
    (state: RootState) => state.fileManagement.isFileDetailsOpen
  );

  const fileIdToFetch = selectedFile?.file_id;

  const { data: fileDetails, isFetching: isFileFetching } = useGetKBFileQuery(
    fileIdToFetch as number,
    { skip: !fileIdToFetch, refetchOnMountOrArgChange: true }
  );

  const [patchFile, { isLoading: isPatching }] = usePatchFileMutation();

  const [file, setFile] = useState<KBFileEntity | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const moreMenuRef = useEventChangeOutside<HTMLDivElement>(() => {
    if (isMoreMenuOpen) setIsMoreMenuOpen(false);
  }, "mousedown");

  const showShimmer = isFileFetching;

  const breadcrumbSegments = buildDrawerBreadcrumbs(path, file?.name || "");

  const handleExpirationDateChange = useCallback(
    async (newDate: Date | null) => {
      if (!file || !newDate) return;

      const { pickerDate: savedDate, pickerTime: savedTime } =
        parseExpirationDateTime(file.file_expiration_time);

      // Compare day-only (the date picker returns midnight while savedDate
      // carries the saved time component), so re-clicking the same day is
      // a no-op and never silently bumps the time.
      if (newDate.toDateString() === savedDate?.toDateString()) return;

      // Time-on-date-change rules:
      //   - Today picked: keep the saved time when still at/after the current
      //     minute, otherwise auto-fill the next quarter-hour slot so the
      //     value lines up with a dropdown option.
      //   - Any future date: always reset the time to 00:00.
      const isPickingToday =
        newDate.toDateString() === new Date().toDateString();
      let finalTime: string;
      if (isPickingToday) {
        const isSavedTimeStillValid =
          savedTime && savedTime >= getCurrentHHMM();
        finalTime = isSavedTimeStillValid
          ? (savedTime as string)
          : getEarliestQuarterHourFromNow();
      } else {
        finalTime = "00:00";
      }

      const newExpirationIso = localDateToUtcIsoString(newDate, finalTime);
      if (newExpirationIso === file.file_expiration_time) return;

      const response = await patchFile({
        file_id: file.file_id,
        file_expiration_time: newExpirationIso,
      }).unwrap();
      setFile(response);
      showSuccessToast(TOAST_FILE_EXPIRATION_UPDATED, {
        position: "bottom-left",
      });
    },
    [file, patchFile]
  );

  const handleExpirationTimeChange = useCallback(
    async (newTime: string) => {
      if (!file) return;

      const { pickerDate: savedDate, pickerTime: savedTime } =
        parseExpirationDateTime(file.file_expiration_time);
      if (newTime === savedTime) return;
      if (!savedDate) return;

      // Block past-time picks when expiration is today.
      // Compare against actual current HH:mm so the user can manually type
      // any future minute (e.g. 19:21 when now is 19:20), even though the
      // dropdown options are limited to quarter-hour slots. Use `<` so the
      // current minute itself stays valid — it still has up to 59 seconds
      // of validity ahead and avoids a race when the clock rolls over
      // while the user is typing or the PATCH is in flight.
      const isToday = savedDate.toDateString() === new Date().toDateString();
      if (isToday && newTime < getCurrentHHMM()) return;

      const newExpirationIso = localDateToUtcIsoString(savedDate, newTime);
      if (newExpirationIso === file.file_expiration_time) return;

      const response = await patchFile({
        file_id: file.file_id,
        file_expiration_time: newExpirationIso,
      }).unwrap();
      setFile(response);
      showSuccessToast(TOAST_FILE_EXPIRATION_UPDATED, {
        position: "bottom-left",
      });
    },
    [file, patchFile]
  );

  const handleClose = useCallback(() => {
    setIsEditingName(false);
    setIsMoreMenuOpen(false);
    dispatch(closeFileDetails());
    // setFile(null);
  }, [dispatch]);

  const handleStartEditing = useCallback(() => {
    if (!file) return;
    setEditedName(file.name);
    setIsEditingName(true);
  }, [file]);

  const handleCancelEditing = useCallback(() => {
    setIsEditingName(false);
    setEditedName("");
  }, []);

  const handleSaveEditing = useCallback(async () => {
    const trimmedName = editedName?.trim();
    if (!file || !trimmedName || trimmedName === file?.name) {
      handleCancelEditing();
      return;
    }
    const response = await patchFile({
      file_id: file?.file_id,
      name: trimmedName,
    }).unwrap();
    setIsEditingName(false);
    setEditedName("");
    setFile(response);
    showSuccessToast(TOAST_FILE_NAME_UPDATED, { position: "bottom-left" });
  }, [file, editedName, patchFile, handleCancelEditing]);

  const handleDownload = useCallback(async () => {
    if (!file || isDownloading) return;
    setIsDownloading(true);
    try {
      const downloadUrl = `${baseUrl}/helpdesk/knowledge-base/files/${file.file_id}/download`;
      await triggerFileDownloadByUrl(downloadUrl, file.name);
      showSuccessToast(TOAST_FILE_DOWNLOAD_SUCCESS, {
        position: "bottom-left",
      });
    } catch {
      showErrorToast(TOAST_FILE_DOWNLOAD_FAILED, { position: "bottom-left" });
    } finally {
      setIsDownloading(false);
      setIsMoreMenuOpen(false);
    }
  }, [file, isDownloading]);

  const handleVisibilityChange = useCallback(
    async (value: string) => {
      if (!file || value === file.visibility) return;
      const response = await patchFile({
        file_id: file.file_id,
        visibility: value as KBFileVisibility,
        viewMode,
      }).unwrap();
      setFile(response);
      showSuccessToast(TOAST_FILE_VISIBILITY_UPDATED, {
        position: "bottom-left",
      });
    },
    [file, patchFile, viewMode]
  );

  useEffect(() => {
    if (fileDetails) setFile(fileDetails);
  }, [fileDetails]);

  const renderBreadcrumbs = () => {
    return (
      <div className="flex items-center gap-1 py-1 px-6">
        {breadcrumbSegments?.map((segment, index) => (
          <div key={index} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronDownIcon className="w-2.5 h-2.5 -rotate-90 text-analytics-gray dark:text-analytics-gray-dk" />
            )}
            <span
              className={`text-md font-semibold ${
                index < breadcrumbSegments.length - 1
                  ? "text-verba-blue dark:text-verba-blue-dk"
                  : "text-analytics-gray dark:text-analytics-gray-dk"
              }`}
            >
              {segment}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const sectionContentMap: Record<string, React.ReactNode> = {
    "access-and-urls": (
      <FileDetailsAccessAndUrls
        visibility={file?.visibility ?? null}
        shortUrl={file?.short_url || ""}
        fileExpirationTime={file?.file_expiration_time}
        onVisibilityChange={handleVisibilityChange}
        onExpirationDateChange={handleExpirationDateChange}
        onExpirationTimeChange={handleExpirationTimeChange}
      />
    ),
    "file-history": (
      <FileDetailsFileHistory
        performedBy={file?.last_updated_by || PLACEHOLDER_EMPTY}
        madeOn={file?.created_at || ""}
      />
    ),
    general: (
      <FileDetailsGeneral
        createdAt={file?.created_at || ""}
        updatedAt={file?.updated_at || ""}
        sizeInBytes={file?.size_in_bytes || 0}
        fileType={file?.type || ""}
        fileId={file?.file_id ?? 0}
      />
    ),
  };

  return (
    <Drawer
      open={isFileDetailsOpen}
      onClose={handleClose}
      direction="right"
      size={FILE_DETAILS_DRAWER_WIDTH}
      overlayOpacity={FILE_DETAILS_OVERLAY_OPACITY}
      drawerContainerClassName="flex flex-col rounded-l-lg"
    >
      {showShimmer ? (
        <FileDetailsDrawerShimmer />
      ) : (
        <>
          <div className="flex items-center justify-between px-6 py-3 bg-hover-effect dark:bg-hover-effect-dk shrink-0">
            <p className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
              {FILE_DETAILS_DRAWER_TITLE}
            </p>
            <button
              className="shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
              aria-label="Close modal"
              onClick={handleClose}
            >
              <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto pt-3">
            <div className="flex flex-col gap-4">
              {renderBreadcrumbs()}

              <div className="flex items-center gap-1.5 px-6">
                {isEditingName ? (
                  <>
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e?.key === "Enter") handleSaveEditing();
                        if (e?.key === "Escape") handleCancelEditing();
                      }}
                      autoFocus
                      style={{
                        width: `${Math.max(editedName?.length || 0, 4) + 2}ch`,
                      }}
                      className="max-w-[500px] text-base font-normal text-verba-indigo dark:text-verba-indigo-dk bg-white dark:bg-transparent border border-border-text-field dark:border-border-text-field-dk rounded-lg px-2 py-0.5 outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleSaveEditing}
                      disabled={isPatching}
                      className="flex items-center justify-center shrink-0 cursor-pointer disabled:cursor-not-allowed"
                      aria-label="Save file name"
                    >
                      {isPatching ? (
                        <ClipLoader
                          size={16}
                          thickness={2}
                          className="text-verba-blue dark:text-verba-blue-dk"
                        />
                      ) : (
                        <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                      {file?.name}
                    </span>
                    {/* <button
                      type="button"
                      onClick={handleStartEditing}
                      className="flex items-center justify-center shrink-0 cursor-pointer"
                      aria-label="Edit file name"
                    >
                      <EditIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
                    </button> */}
                  </>
                )}
              </div>

              <div className="px-6">
                {file && <FilePreviewSection file={file} />}
              </div>

              <FileDetailsAccordion
                key={fileIdToFetch}
                sections={FILE_DETAILS_SECTIONS}
                contentMap={sectionContentMap}
              />
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default FileDetailsDrawer;
